"use client";

import Link from "next/link";
import type { UseFormReturn } from "react-hook-form";

import { PaymentMethodType } from "@gsa/db/schema";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@gsa/ui/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@gsa/ui/components/ui/radio-group";
import { CreditCardIcon, Icons } from "@gsa/ui/icons";
import { cn } from "@gsa/ui/lib/utils";

import { P } from "~/app/ui/typography";
import type { CreateGuestOrderSchema } from "~/lib/types";

export function PaymentTypeRadioGroup({
  form,
  // paymentMethodType,
  // setPaymentMethodType,
  methods,
}: {
  form: UseFormReturn<CreateGuestOrderSchema>;
  // paymentMethodType: PaymentMethodType | undefined;
  // setPaymentMethodType: (paymentMethodType: PaymentMethodType) => void;
  methods: PaymentMethodType[];
}) {
  return (
    <div className="grid gap-6">
      <FormField
        control={form.control}
        name="payment.type"
        render={({ field }) => (
          <FormItem className="col-span-4 ">
            <RadioGroup
              onValueChange={field.onChange}
              defaultValue={field.value}
              className={cn(
                "grid  gap-4",
                methods.length === 4 && "grid-cols-4",
                methods.length === 3 && "grid-cols-3",
                methods.length === 2 && "grid-cols-2",
                methods.length === 1 && "grid-cols-1",
              )}
            >
              {methods.includes(PaymentMethodType.CeleroCreditCard) && (
                <FormItem>
                  <FormControl>
                    <RadioGroupItem
                      className="sr-only"
                      value={PaymentMethodType.CeleroCreditCard}
                    />
                  </FormControl>
                  <FormLabel
                    className={cn(
                      "flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground ",
                      field.value === PaymentMethodType.CeleroCreditCard &&
                        "border-primary",
                    )}
                  >
                    <CreditCardIcon className="h-6 w-6" />
                    Card
                  </FormLabel>
                </FormItem>
              )}

              {methods.includes(PaymentMethodType.AuthorizeCreditCard) && (
                <FormItem>
                  <FormControl>
                    <RadioGroupItem
                      className="sr-only"
                      value={PaymentMethodType.AuthorizeCreditCard}
                    />
                  </FormControl>
                  <FormLabel
                    className={cn(
                      "flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground ",
                      field.value === PaymentMethodType.AuthorizeCreditCard &&
                        "border-primary",
                    )}
                  >
                    <CreditCardIcon className="h-6 w-6" />
                    Card
                  </FormLabel>
                </FormItem>
              )}

              {methods.includes(
                PaymentMethodType.AuthorizeCustomerCreditCard,
              ) && (
                <FormItem>
                  <FormControl>
                    <RadioGroupItem
                      className="sr-only"
                      value={PaymentMethodType.AuthorizeCustomerCreditCard}
                    />
                  </FormControl>
                  <FormLabel
                    className={cn(
                      "flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground ",
                      field.value ===
                        PaymentMethodType.AuthorizeCustomerCreditCard &&
                        "border-primary",
                    )}
                  >
                    <CreditCardIcon className="h-6 w-6" />
                    Card
                  </FormLabel>
                </FormItem>
              )}

              {methods.includes(PaymentMethodType.Cash) && (
                <FormItem>
                  <FormControl>
                    <RadioGroupItem
                      className="sr-only"
                      value={PaymentMethodType.Cash}
                    />
                  </FormControl>
                  <FormLabel
                    className={cn(
                      "flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground ",
                      field.value === PaymentMethodType.Cash &&
                        "border-primary",
                    )}
                  >
                    <Icons.cash className="h-6 w-6 text-foreground" />
                    Cash
                  </FormLabel>
                </FormItem>
              )}

              {methods.includes(PaymentMethodType.Check) && (
                <FormItem>
                  <FormControl>
                    <RadioGroupItem
                      className="sr-only"
                      value={PaymentMethodType.Check}
                    />
                  </FormControl>
                  <FormLabel
                    className={cn(
                      "flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground ",
                      field.value === PaymentMethodType.Check &&
                        "border-primary",
                    )}
                  >
                    <Icons.check className="h-6 w-6 text-foreground" />
                    Check
                  </FormLabel>
                </FormItem>
              )}

              {methods.includes(PaymentMethodType.Wire) && (
                <FormItem>
                  <FormControl>
                    <RadioGroupItem
                      className="sr-only"
                      value={PaymentMethodType.Wire}
                    />
                  </FormControl>
                  <FormLabel
                    className={cn(
                      "flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground ",
                      field.value === PaymentMethodType.Wire &&
                        "border-primary",
                    )}
                  >
                    <Icons.wire className="h-6 w-6 text-foreground" />
                    Wire Transfer
                  </FormLabel>
                </FormItem>
              )}
            </RadioGroup>
          </FormItem>
        )}
      />

      <div className="grid gap-4">
        {form.watch("payment.type") === PaymentMethodType.Check && (
          <P className="text-sm">
            If paying by check, your order will be shipped based on us recieving
            payment and verfication of funds.
          </P>
        )}

        {form.watch("payment.type") === PaymentMethodType.Wire && (
          <P className="text-sm">
            If paying by wire transfer, your order will be shipped based on us
            recieving payment and verfication of funds.
          </P>
        )}

        <P className="text-sm">
          View more information about our payment options on our{" "}
          <Link
            href="https://www.goldstandardx.com/payment-options"
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-500 underline"
          >
            payment options page
          </Link>
          .
        </P>
      </div>
    </div>
  );
}
